import type { ApiFormQuestion, ApiFormQuestionOption } from 'src/core/models/api/shared/ApiFormQuestion';

/**
 * Capacity rules for a choice question, shared by the single- and multiple-choice fields so both answer "is
 * this still available?" the same way (#402).
 */

/**
 * Whether an option has no places left.
 */
export function limitReached(option: ApiFormQuestionOption): boolean {
	const limit = option.limit ?? 0;

	return limit > 0 && option.total_submissions >= limit;
}

/**
 * How many places are left, or null when the option is unlimited.
 */
export function seatsLeft(option: ApiFormQuestionOption): number | null {
	const limit = option.limit ?? 0;

	return limit > 0 ? Math.max(limit - option.total_submissions, 0) : null;
}

/**
 * Whether this question refuses another tick.
 */
export function maxChoicesReached(question: ApiFormQuestion, chosenCount: number): boolean {
	const max = question.max_choices ?? 0;

	return max > 0 && chosenCount >= max;
}

/**
 * Whether this day already has as many picks as it may have. A question over several days is usually one per
 * day, so picking on day 1 must not block day 2 (#404).
 */
export function dayLimitReached(
	question: ApiFormQuestion,
	option: ApiFormQuestionOption,
	chosen: ApiFormQuestionOption[],
): boolean {
	const max = question.max_per_day ?? 0;

	if (max <= 0) return false;

	const day = option.day_ref ?? '';

	return chosen.filter((picked) => (picked.day_ref ?? '') === day).length >= max;
}

/**
 * The pick this option would run into, or null when it fits. Two choices that merely touch (one ends when the
 * next begins) do not overlap.
 */
export function overlapWith(
	question: ApiFormQuestion,
	option: ApiFormQuestionOption,
	chosen: ApiFormQuestionOption[],
): ApiFormQuestionOption | null {
	if (question.allow_overlap) return null;

	const start = option.starts_at;
	const end = option.ends_at;

	if (!start || !end) return null;

	return (
		chosen.find((picked) => {
			if (picked.name === option.name || !picked.starts_at || !picked.ends_at) return false;

			return start < picked.ends_at && picked.starts_at < end;
		}) ?? null
	);
}
